import React, { useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';
import { Icon } from '@swf/sports/components';

import { TXTWCL_Close, TXTWCL_Multiple_bet_information } from './InfoTooltip.messages.json';
import styles from './InfoTooltip.scss';

interface Props {
  parentRef?: React.RefObject<HTMLDivElement>;
}

const InfoTooltip: React.FC<Props> = ({ children, parentRef }) => {
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const selectedPromotionPicker = useSelector(select.betSlip.getSelectedPromotionPicker);

  const { formatMessage } = useRegulatoryIntl();

  useOnClickOutside([popperElement, referenceElement], () => setIsVisible(false));

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: { offset: [0, 11] }
      },
      {
        name: 'preventOverflow',
        options: { boundary: parentRef?.current ?? undefined, padding: 8 }
      },
      {
        name: 'hide' // Tracks if the button is in the view
      }
    ]
  });

  // Hides the tooltip when button is out of the view
  useEffect(() => {
    if (attributes.popper?.['data-popper-reference-hidden']) {
      setIsVisible(false);
    }
  }, [attributes.popper]);

  const toggleTooltip = () => setIsVisible((currentState) => !currentState);

  return (
    <>
      <button
        type="button"
        ref={setReferenceElement}
        onClick={toggleTooltip}
        className={styles.button}
        aria-label={formatMessage(TXTWCL_Multiple_bet_information)}
        disabled={Boolean(selectedPromotionPicker)}
      >
        <Icon name="info" className={styles.icon} size="xl" />
      </button>
      {isVisible && (
        <div
          ref={setPopperElement}
          style={popperStyles.popper}
          {...attributes.popper}
          className={styles.content}
        >
          {children}
          <div ref={setArrowElement} style={popperStyles.arrow} className={styles.arrow} />
          <button
            type="button"
            onClick={toggleTooltip}
            title={formatMessage(TXTWCL_Close)}
            className={styles.close}
          >
            <Icon name="close-medium" size="lg" />
          </button>
        </div>
      )}
    </>
  );
};

export default InfoTooltip;
